import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Divider,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Breadcrumbs,
  Link,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Home,
  ShoppingCart,
  LocalShipping,
  CreditCard,
  Security,
  CheckCircle,
  LocationOn,
  Payment,
  Receipt,
  ArrowBack,
  ArrowForward,
  Add,
  Edit,
  Delete,
  Work,
  Business,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

import {
  selectCartItems,
  selectCartTotals,
  selectCartItemCount,
  clearCart,
  removeCartItem,
  getCartItemAmount,
  getCartItemSelectedSft,
} from '../store/slices/cartSlice';
import { createOrder } from '../store/slices/orderSlice';
import { addressService } from '../services/addressService';
import lotRequestService from '../services/lotRequestService';
import { getFullImageUrl } from '../utils/imageUtils';

// Validation schemas
const shippingSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().required('Phone number is required'),
  address: yup.string().required('Address is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  pincode: yup.string().required('Pincode is required'),
  country: yup.string().required('Country is required'),
});

// Utility functions for address handling
const getAddressTypeIcon = (addressType) => {
  switch (addressType) {
    case 'HOME':
      return <Home fontSize="small" />;
    case 'WORK':
      return <Work fontSize="small" />;
    case 'BUSINESS':
      return <Business fontSize="small" />;
    default:
      return <LocationOn fontSize="small" />;
  }
};

const getAddressTypeLabel = (addressType) => {
  switch (addressType) {
    case 'HOME':
      return 'Home';
    case 'WORK':
      return 'Work';
    case 'BUSINESS':
      return 'Business';
    default:
      return 'Other';
  }
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // Get selected items from navigation state or fallback to all cart items
  const allCartItems = useSelector(selectCartItems);
  const cartTotals = useSelector(selectCartTotals);
  const selectedItemsFromState = location.state?.selectedItems;
  const cartItems = selectedItemsFromState && selectedItemsFromState.length > 0 
    ? selectedItemsFromState 
    : allCartItems;
  
  // Calculate totals for selected items only, including discount from cart page or Redux
  const calculateSelectedItemsTotals = (items) => {
    // Regular items are priced by selected SFT × price per SFT (not price × slabs)
    const subtotal = items.reduce((sum, item) => sum + getCartItemAmount(item), 0);
    
    const gst = subtotal * 0.18; // 18% GST

    const shipping = 0; // Shipping cost will be communicated separately within 24 hours

    // Use discount from location.state (passed from cart) if available, otherwise use Redux
    const discount = location.state?.appliedDiscount !== undefined 
      ? location.state.appliedDiscount 
      : (cartTotals.discount || 0);
    
    // Use discountMeta from location.state if available, otherwise use Redux
    const discountMeta = location.state?.discountMeta || cartTotals.discountMeta;
    const couponCode = location.state?.couponCode || cartTotals.couponCode;
    
    const total = subtotal + gst + shipping - discount;
    
    return {
      subtotal,
      gst,
      shipping,
      discount,
      total,
      itemCount: items.length,
      totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
      discountMeta,
      couponCode,
    };
  };
  
  const selectedCartTotals = calculateSelectedItemsTotals(cartItems);
  const itemCount = cartItems.length;
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { loading: orderLoading } = useSelector((state) => state.orders);
  
  const [activeStep, setActiveStep] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [billingAddressSame, setBillingAddressSame] = useState(true);
  const [orderConfirmationOpen, setOrderConfirmationOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [processing, setProcessing] = useState(false);
  // Payment processing state
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  
  // Address management state
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [useNewAddress, setUseNewAddress] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(false);
  
  const {
    control: shippingControl,
    handleSubmit: handleShippingSubmit,
    formState: { errors: shippingErrors },
    watch: watchShipping,
    setValue: setShippingValue,
  } = useForm({
    resolver: yupResolver(shippingSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phoneNumber || '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
    },
  });
  
  // Fetch user addresses
  const fetchUserAddresses = async () => {
    if (!user?.id) return;
    
    try {
      setAddressesLoading(true);
      const response = await addressService.getUserAddresses(user.id);
      if (response.success) {
        setSavedAddresses(response.addresses || []);
        // Auto-select default address if available
        const defaultAddress = response.addresses?.find(addr => addr.default || addr.isDefault);
        if (defaultAddress && !useNewAddress) {
          setSelectedAddressId(defaultAddress.id);
          handleAddressSelection(defaultAddress.id);
        }
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
      toast.error('Failed to load saved addresses');
    } finally {
      setAddressesLoading(false);
    }
  };
  
  // Redirect if cart is empty, fetch addresses for authenticated users
  useEffect(() => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      navigate('/cart');
      return;
    }
    
    // Fetch user addresses only for authenticated users
    if (isAuthenticated && user?.id) {
      fetchUserAddresses();
    }
  }, [isAuthenticated, cartItems.length, navigate, user?.id]);

  // Handle payment method changes when lot booking items are present
  useEffect(() => {
    const hasLotBookingItems = cartItems.some(item => item.isLotBooking || item.isAdvanceBooking);
    
    // If COD is selected but cart has lot booking items, switch to Razorpay
    if (hasLotBookingItems && paymentMethod === 'cod') {
      setPaymentMethod('razorpay');
      toast.info('COD is not available for lot booking orders. Payment method changed to Razorpay.');
    }
  }, [cartItems, paymentMethod]);
  
  const steps = [
    {
      label: 'Shipping Information',
      icon: <LocalShipping />,
    },
    {
      label: 'Payment Method',
      icon: <CreditCard />,
    },
    {
      label: 'Review & Place Order',
      icon: <Receipt />,
    },
  ];
  
  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry',
  ];
  
  const addressTypes = [
    {
      id: 'HOME',
      name: 'Home',
      icon: <Home />,
      description: 'Home address',
    },
    {
      id: 'WORK',
      name: 'Work',
      icon: <Work />,
      description: 'Office address',
    },
    {
      id: 'BUSINESS',
      name: 'Business',
      icon: <Business />,
      description: 'Business address',
    },
    {
      id: 'OTHER',
      name: 'Other',
      icon: <LocationOn />,
      description: 'Other address',
    },
  ];
  
  // Check if cart contains any lot booking items
  const hasLotBookingItems = cartItems.some(item => item.isLotBooking || item.isAdvanceBooking);
  
  const paymentMethods = [
    {
      id: 'razorpay',
      name: 'Pay with Razorpay',
      description: 'Credit/Debit Card, UPI, Net Banking, Wallets',
      icon: '💳',
    },
    // Only show COD option if there are no lot booking items
    // ...(hasLotBookingItems ? [] : [{
    //   id: 'cod',
    //   name: 'Cash on Delivery',
    //   description: 'Pay when your order is delivered',
    //   icon: '💵',
    // }]),
  ];
  
  const handleNext = () => {
    // If we're on the payment step (step 1), validate payment fields
    if (activeStep === 1) {
      const paymentErrors = validatePaymentFields();
      if (paymentErrors.length > 0) {
        // Show validation errors
        paymentErrors.forEach(error => {
          toast.error(error);
        });
        return;
      }
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  
  const onShippingSubmit = (data) => {
    console.log('Shipping data:', data);
    handleNext();
  };
  
  const handlePaymentMethodChange = (event) => {
    const value = event.target.value;
    setPaymentMethod(value);
  };
  
  // Map frontend payment ids to backend-friendly names
  const mapPaymentMethodToBackend = (id) => {
    switch (id) {
      case 'razorpay':
        return 'RAZORPAY';
      case 'cod':
        return 'COD';
      default:
        return id?.toUpperCase() || '';
    }
  };

  // Validate payment method specific fields
  const validatePaymentFields = () => {
    const errors = [];

    if (!paymentMethod) {
      errors.push('Please select a payment method');
    }

    return errors;
  };

  // Address handling functions
  const handleAddressSelection = (addressId) => {
    setSelectedAddressId(addressId);
    setUseNewAddress(false);
    
    // Find the selected address and populate form
    const selectedAddress = savedAddresses.find(addr => addr.id === addressId);
    if (selectedAddress) {
      setShippingValue('firstName', selectedAddress.firstName || user?.firstName || '');
      setShippingValue('lastName', selectedAddress.lastName || user?.lastName || '');
      setShippingValue('email', selectedAddress.email || user?.email || '');
      setShippingValue('phone', selectedAddress.phone || user?.phoneNumber || '');
      setShippingValue('address', selectedAddress.street || '');
      setShippingValue('city', selectedAddress.city || '');
      setShippingValue('state', selectedAddress.state || '');
      setShippingValue('pincode', selectedAddress.postalCode || '');
      setShippingValue('country', selectedAddress.country || 'India');
    }
  };

  const handleUseNewAddress = () => {
    setUseNewAddress(true);
    setSelectedAddressId(null);
    
    // Reset form to default values
    setShippingValue('firstName', user?.firstName || '');
    setShippingValue('lastName', user?.lastName || '');
    setShippingValue('email', user?.email || '');
    setShippingValue('phone', user?.phoneNumber || '');
    setShippingValue('address', '');
    setShippingValue('city', '');
    setShippingValue('state', '');
    setShippingValue('pincode', '');
    setShippingValue('country', 'India');
  };

  const handlePlaceOrder = async () => {
    // Allow both authenticated and guest users to place orders
    setProcessing(true);

    try {
      console.log('Starting order placement...');
      console.log('Payment method:', paymentMethod);
      console.log('User:', user);
      console.log('Cart items:', cartItems);
      console.log('Cart totals:', selectedCartTotals);
      
      const shippingData = watchShipping();
      const billingData = billingAddressSame ? shippingData : {};
      
      console.log('Shipping data:', shippingData);
      
      // Transform cart items to order items format
      const orderItems = cartItems.map(item => ({
        productId: item.id,
        productName: item.name,
        sellerId: item.sellerId,
        sellerName: item.sellerName,
        quantity: item.quantity,
        // Regular items: unitPrice is the per-SFT price and totalPrice = selectedSft × unitPrice (same as the app)
        unitPrice: getCartItemSelectedSft(item) > 0 ? Number(item.pricePerSft) : (item.discountedPrice || item.price),
        totalPrice: getCartItemAmount(item),
        // Lot bookings carry the SFT of the booked lots
        selectedSft: item.isLotBooking
          ? (Number(item.lotSft) > 0 ? Number(Number(item.lotSft).toFixed(4)) : undefined)
          : (getCartItemSelectedSft(item) > 0 ? getCartItemSelectedSft(item) : undefined),
        productImageUrl: item.image,
        category: item.category,
        subCategory: item.subCategory,
        priceUnit: item.priceUnit,
        lotBooking: item.isLotBooking || false, // Include lot booking flag (use 'lotBooking' for backend)
        advanceBooking: item.isAdvanceBooking || false, // Use 'advanceBooking' for backend (matches @JsonProperty)
        advanceAmount: item.paymentSplit?.advanceAmount,
        remainingAmount: item.paymentSplit?.remainingAmount,
        advancePercentage: item.advancePercentage || 0,
        originalPrice: item.originalPrice
      }));

      if (paymentMethod === 'razorpay') {
        // Razorpay payment flow - support both authenticated and guest users
        const customerName = user?.id 
          ? `${user.firstName || ''} ${user.lastName || ''}`.trim() 
          : `${shippingData.firstName || ''} ${shippingData.lastName || ''}`.trim();
        const customerEmail = user?.email || shippingData.email;
        const customerPhone = user?.phoneNumber || shippingData.phone;
        
        const razorpayOrderData = {
          amount: Math.round(selectedCartTotals.total * 100), // Convert to paise
          currency: 'INR',
          receipt: `receipt_${Date.now()}`,
          notes: {
            customer_name: customerName,
            customer_email: customerEmail,
            customer_phone: customerPhone,
            order_type: 'ecommerce',
            platform: 'web'
          },
          prefill: {
            name: customerName,
            email: customerEmail,
            contact: customerPhone,
            firstName: shippingData.firstName,
            lastName: shippingData.lastName
          }
        };

        console.log('Creating Razorpay order:', razorpayOrderData);
        
        // Call backend to create Razorpay order
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8083/api'}/payments/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('token') && {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            })
          },
          body: JSON.stringify(razorpayOrderData)
        });

        if (!response.ok) {
          throw new Error('Failed to create Razorpay order');
        }

        const razorpayOrder = await response.json();
        console.log('Razorpay order created:', razorpayOrder);

        const paymentsApiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8083/api'}/payments`;

        // Report checkout events that never reach the success handler so the payment record is updated
        const reportCheckoutEvent = (path, body) =>
          fetch(`${paymentsApiUrl}/${path}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(localStorage.getItem('token') && {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              })
            },
            body: JSON.stringify({ razorpay_order_id: razorpayOrder.id, ...body })
          })
            .then(res => (res.ok ? res.json() : null))
            .catch(error => {
              console.error(`Failed to report payment ${path}:`, error);
              return null;
            });

        // Limits come from the backend (razorpay.timeout, razorpay.checkout.max-attempts)
        const checkoutTimeoutSeconds = razorpayOrder.timeout || 300;
        const maxFailedAttempts = razorpayOrder.max_attempts || 3;
        const checkoutOpenedAt = Date.now();
        let rzp = null;
        let checkoutFinished = false; // set once success or a close reason has been handled
        let closeReason = null;
        let failedAttempts = 0;
        let lastFailureReport = Promise.resolve();
        let checkoutTimer = null;

        // Closing the tab or app gives no ondismiss; sendBeacon still delivers while the page unloads.
        // text/plain keeps it a CORS-simple request (no preflight, which an unloading page can't wait for).
        const onPageHide = () => {
          if (checkoutFinished) return;
          checkoutFinished = true;
          clearTimeout(checkoutTimer);
          const beaconBody = JSON.stringify({ razorpay_order_id: razorpayOrder.id, reason: 'page_closed' });
          navigator.sendBeacon(`${paymentsApiUrl}/dismissed`, new Blob([beaconBody], { type: 'text/plain' }));
        };

        const stopCheckoutWatchers = () => {
          clearTimeout(checkoutTimer);
          window.removeEventListener('pagehide', onPageHide);
        };

        // Records why checkout closed without a payment; runs once whichever path gets here first
        const finishCheckout = (reason) => {
          if (checkoutFinished) return;
          checkoutFinished = true;
          stopCheckoutWatchers();
          // Let the last failed attempt be stored before the close reason
          lastFailureReport.then(() => reportCheckoutEvent('dismissed', { reason }));
          if (reason === 'timeout') {
            toast.warning('Payment time expired. Please place the order again.');
          } else if (reason === 'attempts_exhausted') {
            toast.error(`Payment failed ${maxFailedAttempts} times. Please try again later or use a different payment method.`);
          }
          setProcessing(false);
        };

        const closeCheckout = (reason) => {
          if (checkoutFinished) return;
          closeReason = reason;
          finishCheckout(reason);
          try {
            rzp?.close();
          } catch (error) {
            console.error('Failed to close Razorpay checkout:', error);
          }
        };

        // Initialize Razorpay checkout with hosted page configuration
        const options = {
          key: razorpayOrder.key, // Public key ID from the backend, always matches the key that created the order
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          name: 'MALL OF STONES',
          description: `Customer: ${customerName}`,
          image: '/mos-logo.jpg',
          order_id: razorpayOrder.id,
          prefill: {
            name: customerName,
            email: customerEmail,
            contact: customerPhone
          },
          notes: {
            customer_name: customerName,
            customer_email: customerEmail,
            customer_phone: customerPhone,
            order_type: 'ecommerce',
            platform: 'web'
          },
          theme: {
            color: '#3f51b5'
          },
          // Razorpay closes checkout itself after this many seconds; checkoutTimer below records it
          timeout: checkoutTimeoutSeconds,
          modal: {
            ondismiss: function() {
              console.log('Payment modal dismissed');
              // Razorpay's own timeout also ends here, possibly just before checkoutTimer fires
              const timedOut = Date.now() - checkoutOpenedAt >= (checkoutTimeoutSeconds - 2) * 1000;
              finishCheckout(closeReason || (timedOut ? 'timeout' : 'customer'));
            },
            // Enable hosted checkout for full payment gateway experience
            escape: false,
            animation: true,
            backdrop_close: false
          },
          handler: async function (response) {
            console.log('Payment successful:', response);
            // Paid: no timeout, page-close or dismissal report may follow
            checkoutFinished = true;
            stopCheckoutWatchers();

            try {
              // Verify payment on backend
              const verifyResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8083/api'}/payments/verify`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(localStorage.getItem('token') && {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  })
                },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature
                })
              });

              if (!verifyResponse.ok) {
                throw new Error('Payment verification failed');
              }

              const verificationResult = await verifyResponse.json();
              // The backend decides; a 200 with success:false means the signature did not verify
              if (!verificationResult.success) {
                throw new Error(verificationResult.error || 'Payment verification failed');
              }
              console.log('Payment verified:', verificationResult);

              // Create order after successful payment
              await createOrderWithPayment(orderItems, shippingData, billingData, {
                paymentMethod:"RAZORPAY",
                paymentGateway: "RAZORPAY",
                
                cardType: response.razorpay_card_type, // Required for Razorpay validation
                status: 'PAID',
                amount: selectedCartTotals.total,
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
                transactionId: response.razorpay_payment_id,
                gatewayTransactionId: response.razorpay_order_id
              });
              
            } catch (error) {
              console.error('Error after payment:', error);
              toast.error('Payment successful but order creation failed. Please contact support.');
              setProcessing(false);
            }
          },
          // Configure for hosted checkout page with all payment methods
          config: {
            display: {
              blocks: {
                banks: {
                  name: 'Pay using Net Banking',
                  instruments: [
                    {
                      method: 'netbanking'
                    }
                  ]
                },
                card: {
                  name: 'Pay using Cards',
                  instruments: [
                    {
                      method: 'card'
                    }
                  ]
                },
                upi: {
                  name: 'Pay using UPI',
                  instruments: [
                    {
                      method: 'upi'
                    }
                  ]
                },
                wallet: {
                  name: 'Pay using Wallets',
                  instruments: [
                    {
                      method: 'wallet'
                    }
                  ]
                },
                emi: {
                  name: 'Pay using EMI',
                  instruments: [
                    {
                      method: 'emi'
                    }
                  ]
                }
              },
              sequence: ['block.card', 'block.upi', 'block.banks', 'block.wallet', 'block.emi'],
              preferences: {
                show_default_blocks: true
              }
            }
          }
        };

        const openRazorpayCheckout = () => {
          rzp = new window.Razorpay(options);
          // Fires for each failed attempt; the modal stays open so the customer can retry,
          // up to maxFailedAttempts (Razorpay web checkout has no retry limit of its own)
          rzp.on('payment.failed', function (failure) {
            console.log('Payment attempt failed:', failure.error);
            failedAttempts += 1;
            lastFailureReport = reportCheckoutEvent('failed', {
              razorpay_payment_id: failure.error?.metadata?.payment_id,
              code: failure.error?.code,
              description: failure.error?.description,
              reason: failure.error?.reason,
              source: failure.error?.source,
              step: failure.error?.step
            }).then(result => {
              // The backend count also covers attempts from other tabs on the same order
              if (result?.attemptsExhausted) {
                closeCheckout('attempts_exhausted');
              }
            });
            if (failedAttempts >= maxFailedAttempts) {
              closeCheckout('attempts_exhausted');
            }
          });
          window.addEventListener('pagehide', onPageHide);
          checkoutTimer = setTimeout(() => closeCheckout('timeout'), checkoutTimeoutSeconds * 1000);
          rzp.open();
        };

        // Load Razorpay script if not already loaded
        if (!window.Razorpay) {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = openRazorpayCheckout;
          script.onerror = () => {
            toast.error('Failed to load payment gateway. Please try again.');
            setProcessing(false);
          };
          document.body.appendChild(script);
        } else {
          openRazorpayCheckout();
        }

      } else if (paymentMethod === 'cod') {
        // COD payment flow - create order directly
        await createOrderWithPayment(orderItems, shippingData, billingData, {
          paymentMethod: 'COD',
          status: 'PENDING',
          amount: selectedCartTotals.total
        });
      } else {
        throw new Error('Please select a payment method');
      }
      
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error(error.message || 'Failed to place order. Please try again.');
      setProcessing(false);
    }
  };

  const createOrderWithPayment = async (orderItems, shippingData, billingData, paymentDetails) => {
    // Allow both authenticated and guest users to place orders
    const orderData = {
      customerId: user?.id || 'guest',
      customerName: `${shippingData.firstName} ${shippingData.lastName}`,
      customerEmail: user?.email || shippingData.email,
      customerPhone: shippingData.phone,
      items: orderItems,
      paymentStatus: paymentDetails?.status || 'PENDING',
      shippingAddress: {
        street: shippingData.address,
        city: shippingData.city,
        state: shippingData.state,
        postalCode: shippingData.pincode,
        country: shippingData.country || 'India'
      },
      billingAddress: billingAddressSame ? {
        street: shippingData.address,
        city: shippingData.city,
        state: shippingData.state,
        postalCode: shippingData.pincode,
        country: shippingData.country || 'India'
      } : {
        street: billingData.address,
        city: billingData.city,
        state: billingData.state,
        postalCode: billingData.pincode,
        country: billingData.country || 'India'
      },
      subtotal: selectedCartTotals.subtotal,
        taxAmount: selectedCartTotals.gst,
        shippingAmount: selectedCartTotals.shipping,
        discountAmount: selectedCartTotals.discount || 0,
        totalAmount: selectedCartTotals.total,
      paymentDetails: paymentDetails,
      paymentMethod: paymentDetails?.paymentMethod || 'UNKNOWN', // Extract payment method from payment details
      orderSource: 'WEB',
      specialInstructions: '',
      notes: selectedCartTotals.discountMeta ? [
        {
          note: JSON.stringify({
            promotionId: selectedCartTotals.discountMeta.promotionId,
            promotionTitle: selectedCartTotals.discountMeta.promotionTitle || selectedCartTotals.couponCode,
            discountType: selectedCartTotals.discountMeta.discountType,
            discountPercentage: selectedCartTotals.discountMeta.discountType === 'percentage' ? selectedCartTotals.discountMeta.value : null,
            discountAmount: selectedCartTotals.discountMeta.discountType === 'amount' ? selectedCartTotals.discountMeta.discountAmount : null,
            appliedDiscount: selectedCartTotals.discount || 0,
          }),
          noteType: 'SYSTEM',
          addedBy: user?.id || 'system',
          addedByRole: 'SYSTEM',
          visible: true,
          customerVisible: false,
        }
      ] : [],
      sellerIds: [...new Set(cartItems.map(item => item.sellerId))],
      multiVendor: [...new Set(cartItems.map(item => item.sellerId))].length > 1
    };
    
    console.log('Order data to be sent:', orderData);
    console.log('[STOCK DEBUG] Order items being sent:', orderData.items?.map(i => ({
      productId: i.productId, quantity: i.quantity, isLotBooking: i.lotBooking
    })));

    // Create order
    const result = await dispatch(createOrder(orderData)).unwrap();
    
    // Generate order ID
    const newOrderId = result.order?.orderNumber || `MOS${Date.now()}`;
    setOrderId(newOrderId);
    
    // Handle lot requests if any - Create lot request AFTER order is successfully created
    const pendingLotRequestData = localStorage.getItem('pendingLotRequest');
    const hasLotBookingItems = cartItems.some(item => item.isLotBooking);
    
    if (pendingLotRequestData && hasLotBookingItems) {
      try {
        // Parse lot request data
        const lotRequestData = JSON.parse(pendingLotRequestData);
        
        // Update lot request with order details
        const updatedLotRequestData = {
          ...lotRequestData,
          // Ensure numeric fields are properly typed
          lotPercentage: Number(lotRequestData.lotPercentage),
          lotQuantity: Number(lotRequestData.lotQuantity),
          baseAmount: Number(lotRequestData.baseAmount),
          gstAmount: Number(lotRequestData.gstAmount),
          gstRate: Number(lotRequestData.gstRate),
          shippingCharges: Number(lotRequestData.shippingCharges),
          advanceAmount: Number(lotRequestData.advanceAmount),
          remainingAmount: Number(lotRequestData.remainingAmount),
          unitPrice: Number(lotRequestData.unitPrice),
          actualPrice: Number(lotRequestData.actualPrice),
          totalLotPrice: Number(lotRequestData.totalLotPrice),
          productWeight: Number(lotRequestData.productWeight),
          
          // Update message with order information
          message: `Lot booking request for ${lotRequestData.productName}. Base Amount: ₹${lotRequestData.baseAmount.toFixed(2)}, Advance (10%): ₹${lotRequestData.advanceAmount.toFixed(2)}, GST on Advance: ₹${lotRequestData.gstAmount.toFixed(2)} - Order #${newOrderId} placed successfully. Payment Method: ${paymentDetails.paymentMethod}, Order Total: ₹${selectedCartTotals.total}${paymentDetails.razorpayPaymentId ? `, Payment ID: ${paymentDetails.razorpayPaymentId}` : ''}`,
          
          status: "PENDING_INSPECTION",
          timeline: null,
          inspectionDate: null,
          approvedAt: null,
          rejectedAt: null,
          expectedDeliveryDate: null,
          actualDeliveryDate: null
        };
        
        // Remove any undefined/null properties
        Object.keys(updatedLotRequestData).forEach(key => {
          if (updatedLotRequestData[key] === undefined || updatedLotRequestData[key] === null) {
            delete updatedLotRequestData[key];
          }
        });
        
        console.log('Creating lot request after order creation:', updatedLotRequestData);
        
        // Create lot request in DB AFTER order is successfully created
        await lotRequestService.createLotRequest(updatedLotRequestData);
        console.log('Lot request created successfully after order');
        
        // Clear the pending lot request data
        localStorage.removeItem('pendingLotRequest');
        
        toast.success('Order placed and lot request created successfully!');
      } catch (lotError) {
        console.error('Error creating lot request:', lotError);
        toast.error('Order placed but failed to create lot request. Please contact support.');
      }
    } else {
      toast.success(`Order placed successfully! Payment method: ${paymentDetails.paymentMethod}`);
    }
    
    // Remove only selected items from cart
    if (selectedItemsFromState && selectedItemsFromState.length > 0) {
      // Remove each selected item from the cart
      selectedItemsFromState.forEach(item => {
        dispatch(removeCartItem(item.itemKey));
      });
    } else {
      // If no specific selection, clear entire cart (fallback)
      dispatch(clearCart());
    }
    
    setOrderConfirmationOpen(true);
    setProcessing(false);
  };
  
  const ShippingForm = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Shipping Information
        </Typography>
        
        {/* Saved Addresses Section */}
        {savedAddresses.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Select Saved Address
            </Typography>
            
            {addressesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {savedAddresses.map((address) => (
                  <Grid item xs={12} sm={6} md={4} key={address.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: selectedAddressId === address.id ? 2 : 1,
                        borderColor: selectedAddressId === address.id ? 'primary.main' : 'grey.300',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleAddressSelection(address.id)}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {getAddressTypeIcon(address.addressType)}
                          <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 600 }}>
                            {getAddressTypeLabel(address.addressType)}
                          </Typography>
                          {address.isDefault && (
                            <Typography
                              variant="caption"
                              sx={{
                                ml: 'auto',
                                px: 1,
                                py: 0.5,
                                bgcolor: 'primary.main',
                                color: 'white',
                                borderRadius: 1,
                              }}
                            >
                              Default
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {address.addressName && `${address.addressName}, `}
                          {address.street}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {address.city}, {address.state} - {address.postalCode}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                
                {/* Add New Address Option */}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: useNewAddress ? 2 : 1,
                      borderColor: useNewAddress ? 'primary.main' : 'grey.300',
                      borderStyle: 'dashed',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 2,
                      },
                    }}
                    onClick={handleUseNewAddress}
                  >
                    <CardContent sx={{ p: 2, textAlign: 'center' }}>
                      <Add sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Typography variant="subtitle2" color="primary.main">
                        Add New Address
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            
            <Divider sx={{ my: 3 }} />
          </Box>
        )}
        
        <form onSubmit={handleShippingSubmit(onShippingSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="firstName"
                control={shippingControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="First Name"
                    error={!!shippingErrors.firstName}
                    helperText={shippingErrors.firstName?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="lastName"
                control={shippingControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Last Name"
                    error={!!shippingErrors.lastName}
                    helperText={shippingErrors.lastName?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={shippingControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email Address"
                    type="email"
                    error={!!shippingErrors.email}
                    helperText={shippingErrors.email?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={shippingControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Phone Number"
                    error={!!shippingErrors.phone}
                    helperText={shippingErrors.phone?.message}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Controller
                name="address"
                control={shippingControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Address"
                    multiline
                    rows={2}
                    error={!!shippingErrors.address}
                    helperText={shippingErrors.address?.message}
                    disabled={!useNewAddress && selectedAddressId}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="city"
                control={shippingControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="City"
                    error={!!shippingErrors.city}
                    helperText={shippingErrors.city?.message}
                    disabled={!useNewAddress && selectedAddressId}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="state"
                control={shippingControl}
                render={({ field }) => (
                  <FormControl fullWidth error={!!shippingErrors.state}>
                    <InputLabel>State</InputLabel>
                    <Select 
                      {...field} 
                      label="State"
                      disabled={!useNewAddress && selectedAddressId}
                    >
                      {indianStates.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="pincode"
                control={shippingControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Pincode"
                    error={!!shippingErrors.pincode}
                    helperText={shippingErrors.pincode?.message}
                    disabled={!useNewAddress && selectedAddressId}
                  />
                )}
              />
            </Grid>
            
            {useNewAddress && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="addressType"
                  control={shippingControl}
                  defaultValue="HOME"
                  render={({ field }) => (
                    <FormControl fullWidth error={!!shippingErrors.addressType}>
                      <InputLabel>Address Type</InputLabel>
                      <Select {...field} label="Address Type">
                        {addressTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getAddressTypeIcon(type.id)}
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {type.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {type.description}
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {shippingErrors.addressType && (
                        <FormHelperText>{shippingErrors.addressType.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            )}
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="country"
                control={shippingControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Country"
                    disabled
                    error={!!shippingErrors.country}
                    helperText={shippingErrors.country?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={() => navigate('/cart')}
              startIcon={<ArrowBack />}
            >
              Back to Cart
            </Button>
            <Button
              type="submit"
              variant="contained"
              endIcon={<ArrowForward />}
            >
              Continue to Payment
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
  
  const PaymentMethodSelection = () => {

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Choose Payment Method
          </Typography>
          
          {/* Unified Payment Method Selector */}
          <RadioGroup
            value={paymentMethod}
            onChange={handlePaymentMethodChange}
          >
            {paymentMethods.map((method) => (
              <Box key={method.id} sx={{ mb: 2 }}>
                <Card
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    border: paymentMethod === method.id ? 2 : 1,
                    borderColor: paymentMethod === method.id ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: 'primary.light',
                      boxShadow: 1,
                    },
                  }}
                  onClick={() => setPaymentMethod(method.id)}
                >
                  <CardContent sx={{ py: 2 }}>
                    <FormControlLabel
                      value={method.id}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                          <Typography sx={{ fontSize: '1.5rem', mr: 2 }}>
                            {method.icon}
                          </Typography>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {method.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {method.description}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{ margin: 0, width: '100%' }}
                    />
                  </CardContent>
                </Card>

                {/* Payment Method Actions */}
                
                {/* Razorpay Payment Flow */}
                {paymentMethod === 'razorpay' && method.id === 'razorpay' && (
                  <Box sx={{ mt: 2, p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      You will be redirected to Razorpay's secure payment gateway to complete your payment using your preferred method (Credit/Debit Card, UPI, Net Banking, Wallets).
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={processing}
                      sx={{ 
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        bgcolor: '#3f51b5',
                        '&:hover': {
                          bgcolor: '#303f9f'
                        }
                      }}
                      onClick={handlePlaceOrder}
                    >
                      {processing ? 'Processing...' : 'Pay Now with Razorpay'}
                    </Button>
                  </Box>
                )}

                {/* COD Payment Flow */}
                {/*paymentMethod === 'cod' && method.id === 'cod' && (
                  <Box sx={{ mt: 2, p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Pay in cash when your order is delivered to your doorstep. Please keep the exact amount ready.
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      fullWidth
                      disabled={processing}
                      sx={{ 
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600
                      }}
                      onClick={handlePlaceOrder}
                    >
                      {processing ? 'Processing...' : 'Place Order (COD)'}
                    </Button>
                  </Box>
                )*/}
              </Box>
            ))}
          </RadioGroup>
          
          {/* Billing Address */}
      <Box sx={{ mt: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={billingAddressSame}
              onChange={(e) => setBillingAddressSame(e.target.checked)}
            />
          }
          label="Billing address is same as shipping address"
        />
      </Box>
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBack />}
        >
          Back to Shipping
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          endIcon={<ArrowForward />}
        >
          Review Order
        </Button>
      </Box>
    </CardContent>
  </Card>
  );
};
  
  const OrderReview = () => (
    <Box sx={{ width: '100%', minHeight: '100vh' }}>
      {/* Back to Payment Button */}
      <Container maxWidth={false} sx={{ px: '20px !important', mb: 3 }}>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBack />}
          sx={{ 
            color: '#6c757d',
            '&:hover': { backgroundColor: '#f8f9fa' }
          }}
        >
          Back to Payment
        </Button>
      </Container>

      {/* Main Content */}
      <Container maxWidth={false} sx={{ 
        px: '20px !important',
        display: 'grid',
        gridTemplateColumns: '1fr 400px',
        gap: '30px',
        alignItems: 'start'
      }}>
        {/* Left Column */}
        <Box>
          {/* Order Items Card */}
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            borderRadius: '12px',
            border: '1px solid #e9ecef',
            mb: 3
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                fontWeight: 600,
                color: '#2c3e50',
                fontSize: '1.25rem'
              }}>
                Order Items ({itemCount} items)
              </Typography>
              
              <Box>
                {cartItems.map((item, index) => (
                  <React.Fragment key={item.itemKey}>
                    <Box sx={{ 
                      display: 'grid',
                      gridTemplateColumns: '80px 1fr auto',
                      gap: 2,
                      alignItems: 'center',
                      py: 2
                    }}>
                      {/* Product Image */}
                      <Box sx={{ position: 'relative' }}>
                        {item.image ? (
                          <Box
                            component="img"
                            src={getFullImageUrl(item.image)}
                            alt={item.name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '1px solid #e9ecef'
                            }}
                          />
                        ) : null}
                        <Box sx={{
                          width: 80,
                          height: 80,
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #e9ecef',
                          borderRadius: '8px',
                          display: item.image ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '2rem'
                        }}>
                          🏛️
                        </Box>
                      </Box>

                      {/* Product Details */}
                      <Box>
                        <Typography variant="subtitle1" sx={{ 
                          fontWeight: 500,
                          color: '#2c3e50',
                          mb: 0.5
                        }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: '#6c757d',
                          mb: 0.5
                        }}>
                          {Number(item.pricePerSft) > 0 && !item.isLotBooking && !item.isAdvanceBooking && item.quantity > 0
                            ? `Quantity: ${item.quantity} · ${(getCartItemAmount(item) / Number(item.pricePerSft)).toFixed(2)} SFT × ₹${Number(item.pricePerSft).toFixed(2)} per SFT + GST`
                            : `Quantity: ${item.quantity} × ₹${(Number(item.discountedPrice || item.price) * 1.18).toFixed(2)} (Incl. GST)`}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: '#6c757d',
                          fontSize: '0.875rem'
                        }}>
                          Seller: {item.sellerName}
                        </Typography>
                      </Box>

                      {/* Price */}
                      <Typography variant="h6" sx={{ 
                        fontWeight: 600,
                        color: '#2c3e50',
                        textAlign: 'right'
                      }}>
                        ₹{(getCartItemAmount(item) * 1.18).toFixed(2)}
                      </Typography>
                    </Box>
                    {index < cartItems.length - 1 && (
                      <Divider sx={{ borderColor: '#e9ecef' }} />
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </CardContent>
          </Card>
          
          {/* Shipping & Payment Card */}
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            borderRadius: '12px',
            border: '1px solid #e9ecef'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                fontWeight: 600,
                color: '#2c3e50',
                fontSize: '1.25rem'
              }}>
                Shipping & Payment
              </Typography>
              
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 4
              }}>
                {/* Shipping Address */}
                <Box>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    fontWeight: 600,
                    color: '#2c3e50'
                  }}>
                    <LocationOn sx={{ mr: 1, fontSize: 20, color: '#007bff' }} />
                    Shipping Address
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#495057',
                    lineHeight: 1.6,
                    fontWeight: 400
                  }}>
                    {watchShipping('firstName')} {watchShipping('lastName')}<br />
                    {watchShipping('address')}<br />
                    {watchShipping('city')}, {watchShipping('state')} {watchShipping('pincode')}<br />
                    {watchShipping('country')}
                  </Typography>
                </Box>
                
                {/* Payment Method */}
                <Box>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    fontWeight: 600,
                    color: '#2c3e50'
                  }}>
                    <CreditCard sx={{ mr: 1, fontSize: 20, color: '#28a745' }} />
                    Payment Method
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#495057',
                    fontWeight: 400
                  }}>
                    {paymentMethods.find(m => m.id === paymentMethod)?.name}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        
        {/* Right Column - Order Summary */}
        <Box sx={{ position: 'sticky', top: '20px' }}>
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            borderRadius: '12px',
            border: '1px solid #e9ecef'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ 
                mb: 3, 
                fontWeight: 600,
                color: '#2c3e50',
                fontSize: '1.25rem'
              }}>
                Order Summary
              </Typography>
              
              <Box>
                {/* Subtotal */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1.5
                }}>
                  <Typography sx={{ color: '#495057', fontWeight: 400 }}>
                    Subtotal
                  </Typography>
                  <Typography sx={{ color: '#2c3e50', fontWeight: 500 }}>
                    ₹{selectedCartTotals.subtotal.toFixed(2)}
                  </Typography>
                </Box>
                
                {/* Tax */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1.5
                }}>
                  <Typography sx={{ color: '#495057', fontWeight: 400 }}>
                    Tax (GST 18%)
                  </Typography>
                  <Typography sx={{ color: '#2c3e50', fontWeight: 500 }}>
                    ₹{selectedCartTotals.gst.toFixed(2)}
                  </Typography>
                </Box>
                
                {/* Discount/Promotion */}
                {selectedCartTotals.discount > 0 && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1.5
                  }}>
                    <Typography sx={{ color: 'success.main', fontWeight: 500 }}>
                      Promotion ({selectedCartTotals.couponCode || 'Discount'})
                    </Typography>
                    <Typography sx={{ color: 'success.main', fontWeight: 600 }}>
                      -₹{selectedCartTotals.discount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                
                <Divider sx={{ my: 2, borderColor: '#e9ecef' }} />
                
                {/* Total */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1.5,
                  borderTop: '1px solid #e9ecef',
                  pt: 2
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                    Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                    ₹{selectedCartTotals.total.toFixed(2)}
                  </Typography>
                </Box>

                <Typography variant="caption" sx={{ display: 'block', color: '#6c757d', mt: 0.5, fontSize: '0.8rem', textAlign: 'right' }}>
                  + Shipping <br/>
                  ( Shipping cost will be calculated once qty of material and its target location is known)
                </Typography>
              </Box>

              {/* Place Order Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handlePlaceOrder}
                disabled={processing}
                sx={{
                  mt: 3,
                  py: 2.25,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  backgroundColor: '#4a5568',
                  borderRadius: '10px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#2d3748'
                  },
                  '&:disabled': {
                    backgroundColor: '#a0aec0'
                  }
                }}
              >
                {processing ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Place Order'
                )}
              </Button>
              
              {/* Security Badge */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: '#6c757d',
                  fontSize: '0.875rem'
                }}>
                  <Security sx={{ fontSize: 16, mr: 0.5 }} />
                  Your payment is secured with 256-bit SSL encryption
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
  
  const OrderConfirmationDialog = () => (
    <Dialog
      open={orderConfirmationOpen}
      onClose={() => setOrderConfirmationOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ textAlign: 'center', py: 4 }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
          Order Placed Successfully!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Thank you for your order. We'll send you a confirmation email shortly.
        </Typography>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Order ID: <strong>{orderId}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You can track your order status in your profile.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          variant="contained"
          onClick={() => {
            setOrderConfirmationOpen(false);
            navigate('/Orders');
          }}
        >
          View Orders
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setOrderConfirmationOpen(false);
            navigate('/');
          }}
        >
          Continue Shopping
        </Button>
      </DialogActions>
    </Dialog>
  );
  
  if (cartItems.length === 0) {
    return null; // Will redirect in useEffect
  }
  
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center' }}>
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Link component={RouterLink} to="/cart" sx={{ display: 'flex', alignItems: 'center' }}>
          <ShoppingCart sx={{ mr: 0.5 }} fontSize="inherit" />
          Cart
        </Link>
        <Typography color="text.primary">
          Checkout
        </Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 4 }}>
        Checkout
      </Typography>
      
      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel icon={step.icon}>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>
      
      {/* Step Content */}
      <motion.div
        key={activeStep}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeStep === 0 && (
          <>
            <ShippingForm />
            <Card sx={{ mt: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e5e5e5' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#2c3e50' }}>
                  Order Summary
                </Typography>

                {/* Items list */}
                <Box sx={{ mb: 2 }}>
                  {cartItems.map((item, index) => (
                    <React.Fragment key={item.itemKey || index}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                          {item.image ? (
                            <Box
                              component="img"
                              src={getFullImageUrl(item.image)}
                              alt={item.name}
                              sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, border: '1px solid #e9ecef', flexShrink: 0 }}
                            />
                          ) : (
                            <Box sx={{ width: 48, height: 48, bgcolor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '1.25rem' }}>
                              🏛️
                            </Box>
                          )}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>
                              {item.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6c757d' }}>
                              Qty: {item.quantity}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50', ml: 2, flexShrink: 0 }}>
                          ₹{(getCartItemAmount(item) * 1.18).toFixed(2)}
                        </Typography>
                      </Box>
                      {index < cartItems.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Price breakdown */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6c757d' }}>Subtotal</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>₹{selectedCartTotals.subtotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6c757d' }}>GST (18%)</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>₹{selectedCartTotals.gst.toFixed(2)}</Typography>
                </Box>
                {selectedCartTotals.discount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                      Promotion ({selectedCartTotals.couponCode || 'Discount'})
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                      -₹{selectedCartTotals.discount.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50' }}>Total</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2c3e50' }}>₹{selectedCartTotals.total.toFixed(2)}</Typography>
                </Box>

                <Typography variant="caption" sx={{ display: 'block', color: '#6c757d', mt: 0.5, fontSize: '0.8rem', textAlign: 'right' }}>
                  + Shipping<br/>
                  (Shipping cost will be calculated once qty of material and its target location is known)
                </Typography>
              </CardContent>
            </Card>
          </>
        )}
        {activeStep === 1 && <PaymentMethodSelection />}
        {activeStep === 2 && <OrderReview />}
      </motion.div>
      
      {/* Order Confirmation Dialog */}
      <OrderConfirmationDialog />
    </Container>
  );
};

export default CheckoutPage;